<template>
  <div
    v-if="transactionStatusPage === TransactionStatus.PENDING"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <div
        class="transaction-status-container"
        :style="{ backgroundColor: transactionStatusData.backgroundColor }"
      >
        <spinner v-if="transactionStatus === TransactionStatus.PENDING || isLoadingOrder" />
        <v-icon v-else :color="transactionStatusData.color" size="40">
          {{ transactionStatusData.icon }}
        </v-icon>
        <div>
          <p
            class="transaction-status-title"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.title }}
          </p>
          <p
            class="transaction-status-description"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.description }}
          </p>
        </div>
      </div>
      <div style="display: flex; flex-direction: column; gap: 0.5rem">
        <span class="my-page-title">Plaćanje u tijeku</span>
        <span v-if="!isLoadingOrder" id="description">
          Vaša narudžba je rezervirana. Molimo pričekajte dok provjeravamo
          <strong>status Vašeg plaćanja</strong>.
        </span>
        <span v-else id="description"> Molimo pričekajte dok učitavamo Vašu narudžbu... </span>
        <span v-if="!isLoadingOrder" id="description">
          Informacije o narudžbi bit će poslane na Vašu e-mail adresu.
        </span>
      </div>
    </div>
  </div>
  <div
    v-else-if="transactionStatusPage === TransactionStatus.FAILED"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <div style="display: flex; flex-direction: column; gap: 0.5rem">
        <span class="my-page-title">Plaćanje nije uspjelo</span>
        <span id="description">
          Nažalost, Vaše plaćanje nije uspješno obrađeno.
          <strong>Molimo pokušajte ponovo</strong> ili kontaktirajte našu podršku.
        </span>
      </div>
    </div>
  </div>
  <div v-else class="registration-success-page-container">
    <div class="registration-success-page">
      <img src="/assets/successful-payment.png" alt="Uspješna kupovina">
      <div style="display: flex; flex-direction: column; gap: 0.5rem">
        <span class="my-page-title">{{ $t('paymentSuccessful') }}</span>
        <span id="description">Za više informacija provjeri svoj e-mail.</span>
        <span id="description">Hvala na povjerenju.</span>
      </div>
      <a
        v-if="user && order"
        target="_blank"
        :href="`${customerURL}/orders/${order.uuid}`"
        style="margin-top: 24px"
      >
        <main-button color="#333" style="border-radius: 0">Pogledaj Narudžbu</main-button>
      </a>
    </div>
    <div v-if="adsLoaded && ads.length" class="shop-section">
      <h4>CoreEvent partner ponuda</h4>
      <div class="shop-cards-section">
        <a
          v-for="ad in ads.slice(0, 3)"
          :key="ad.url"
          class="shop-card"
          target="_blank"
          :href="ad.url"
        >
          <img v-if="ad.logoUrl" class="logo-img" :src="ad.logoUrl">
          <img class="product-img" :src="ad.imageUrl">
          <div class="shop-card-details">
            <h5>{{ ad.title }}</h5>
          </div>
        </a>
      </div>
    </div>
    <div v-else-if="adsLoaded">
      <img src="/assets/successful-payment.svg">
    </div>
  </div>
</template>

<script>
import { getOrganizationStoreOrderInfoByUUID, getPaymentStatusToken } from '@/http'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { CHECK_ORDER_WS_PAY_STATUS } from '@/graphql/order.graphql'
import { customerURL, landingURL } from '@/utils'
import { TransactionStatus } from '@/types'

export default {
  name: 'store-successful-payment',
  components: {},
  data() {
    return {
      ticketDownloaded: false,
      ticketError: false,
      customerURL,
      landingURL,
      user: null,
      order: null,
      ads: [],
      adsLoaded: false,
      transactionStatusPage: TransactionStatus.PENDING,
      transactionStatus: TransactionStatus.PENDING,
      pollCount: 0,
      pollTimer: null,
      loading: false, // Changed to false so we can show the pending UI
      isFreeOrder: false,
      orderFetchPollCount: 0,
      orderFetchPollTimer: null,
      orderFetchError: false,
      isLoadingOrder: true, // New flag to track if we're still fetching the order
    }
  },
  computed: {
    TransactionStatus() {
      return TransactionStatus
    },
    transactionStatusData() {
      if (this.transactionStatus === TransactionStatus.PENDING) {
        return {
          title: 'Provjeravamo plaćanje',
          description: 'Molimo pričekajte...',
          backgroundColor: '#E6F1F8',
          color: '#0277BD',
          icon: '',
        }
      } else if (this.transactionStatus === TransactionStatus.SUCCESSFUL) {
        return {
          title: 'Plaćanje uspješno',
          description: 'Vaša transakcija je uspješno obrađena',
          backgroundColor: '#EEF6EF',
          color: '#0E7E1A',
          icon: 'check_circle',
        }
      } else if (this.transactionStatus === TransactionStatus.FAILED) {
        return {
          title: 'Plaćanje neuspješno',
          description: 'Transakcija nije uspjela',
          backgroundColor: '#FBEBED',
          color: '#BD2A3C',
          icon: 'error',
        }
      }
    },
  },
  async mounted() {
    localStorage.removeItem('organizationStoreOrder')
    localStorage.removeItem('organizationSubscriptionStoreOrder')
    localStorage.removeItem('organizationMembershipStoreOrder')
    await this.fetchOrderWithPolling()
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopOrderFetchPolling()
  },
  methods: {
    async fetchOrderWithPolling() {
      const uuid = this.$router.history.current.params.orderUUID

      try {
        this.order = await getOrganizationStoreOrderInfoByUUID(uuid)

        if (this.order) {
          // Successfully fetched order, stop polling and process it
          this.isLoadingOrder = false
          this.stopOrderFetchPolling()
          await this.processOrder()
        } else {
          // Order is null but no error - might be invalid UUID
          // After several retries with null response, it's probably a bad UUID
          if (this.orderFetchPollCount >= 5) {
            // If consistently null, redirect to 404
            this.$router.push('/404')
          } else {
            this.scheduleNextOrderFetch()
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error)
        this.orderFetchError = true
        this.scheduleNextOrderFetch()
      }
    },

    scheduleNextOrderFetch() {
      this.orderFetchPollCount++

      let interval
      if (this.orderFetchPollCount < 10) {
        // First 10 calls: every 2 seconds
        interval = 2000
      } else if (this.orderFetchPollCount < 20) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.orderFetchPollCount < 30) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // After 30 attempts: every 30 seconds indefinitely
        interval = 30000
      }

      this.orderFetchPollTimer = setTimeout(() => {
        this.fetchOrderWithPolling()
      }, interval)
    },

    stopOrderFetchPolling() {
      if (this.orderFetchPollTimer) {
        clearTimeout(this.orderFetchPollTimer)
        this.orderFetchPollTimer = null
      }
    },

    async processOrder() {
      this.isFreeOrder =
        this.order.paymentType === 'FREE' || ['EUR 0', 'EUR 0.00'].includes(this.order.price)

      if (this.isFreeOrder) {
        this.transactionStatus = TransactionStatus.SUCCESSFUL
        this.transactionStatusPage = TransactionStatus.SUCCESSFUL
      }

      const { data = {} } = await this.getUser()
      const { me: user } = data || {}
      this.user = user

      // Start polling for WsPay status if transaction is pending
      if (!this.isFreeOrder && this.transactionStatusPage === TransactionStatus.PENDING) {
        this.startPolling()
      }
    },

    async getUser() {
      return this.$apollo.query({
        query: GET_LOGGED_USER,
      })
    },
    async checkWsPayStatus() {
      try {
        // Prepare the order items for the query
        const items = this.order.purchases.map(purchase => ({
          orderItemId: purchase.orderItemId,
          count: 1,
        }))

        // Group items by orderItemId to get the count
        const groupedItems = items.reduce((acc, item) => {
          const existing = acc.find(i => i.orderItemId === item.orderItemId)
          if (existing) {
            existing.count += 1
          } else {
            acc.push({ ...item })
          }

          return acc
        }, [])

        const { data } = await this.$apollo.query({
          query: CHECK_ORDER_WS_PAY_STATUS,
          variables: {
            order: {
              uuid: this.order.uuid,
              orderId: this.order.id,
              statusToken: getPaymentStatusToken(this.order.uuid),
              eventId: null,
              items: groupedItems,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (data && data.checkOrderWsPayStatus && data.checkOrderWsPayStatus.success) {
          // Payment successful
          this.transactionStatus = TransactionStatus.SUCCESSFUL
          setTimeout(() => {
            this.transactionStatusPage = TransactionStatus.SUCCESSFUL
          }, 2000)
          this.stopPolling()
        } else {
          // Continue polling
          this.scheduleNextPoll()
        }
      } catch (error) {
        console.error('Error checking WsPay status:', error)
        this.scheduleNextPoll()
      }
    },
    scheduleNextPoll() {
      this.pollCount++

      let interval
      if (this.pollCount < 20) {
        // First 20 calls: every 3 seconds
        interval = 3000
      } else if (this.pollCount < 30) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.pollCount < 40) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // A timeout is not proof of payment failure. Keep reconciling slowly
        // until the backend confirms a terminal state or this page closes.
        interval = 30000
      }

      this.pollTimer = setTimeout(() => {
        this.checkWsPayStatus()
      }, interval)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    startPolling() {
      // Start polling immediately
      this.pollCount = 0
      this.checkWsPayStatus()
    },
  },
}
</script>

<style scoped>
.my-page-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 2.25rem;
  line-height: 2.8rem;
  color: #333;
  margin-bottom: 1.5rem;
}
.registration-success-page-container {
  margin: auto;
  max-width: 1440px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6rem;
}

.registration-success-page {
  align-items: flex-start;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

#description {
  font-family: Source Sans Pro, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 23px;
  color: #333333;
}

@media screen and (max-width: 1187px) {
  .registration-success-page-container {
    padding: 0 1rem;
    justify-content: center;
  }

  .registration-success-page {
    padding-bottom: 4rem;
  }

  .registration-success-page-container .shop-section {
    max-width: 38rem;
    width: calc(100% + 2.5rem);
    align-self: flex-end;
    margin-right: -2.5rem;
    border-radius: 1.5rem 0 0 1.5rem;
  }

  .shop-cards-section {
    overflow-x: scroll;
  }
}

.v-btn--disabled {
  border: none;
}

#download-ticket .v-progress-circular {
  margin-left: 10px;
}

.shop-section {
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #cee4f2;
  border-radius: 1.5rem;
}

.shop-section h4 {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
  padding-bottom: 1.5rem;
}

.shop-cards-section {
  display: flex;
}

.shop-card:nth-of-type(1),
.shop-card:nth-of-type(2) {
  margin-right: 1.5rem;
}

.shop-card {
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  background: #f0f6fa;
  align-items: flex-start;
  max-width: 10.3rem;
}

.shop-card-details {
  padding: 16px 18px 24px;
}

.shop-card-details h5 {
  color: #0277bd;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
}

.shop-card .logo-img {
  position: absolute;
  height: 2rem;
  width: auto;
  border-radius: 1rem 0;
}
.shop-card .product-img {
  border-radius: 1rem 1rem 0 0;
  height: 10.3rem;
  width: 10.3rem;
}

.transaction-status-container {
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 0.75rem;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  border-radius: 62.4375rem;
  margin-bottom: 1.5rem;
}

.transaction-status-title {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 110%;
}

.transaction-status-description {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
}
</style>
